import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Chip, CircularProgress, FormControlLabel,
  Paper, Stack, Switch, TextField, Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import authService from '../../api-authorization/AuthorizeService';
import toast from 'react-hot-toast';
import styles from './Notifications.module.css';

const BASE = process.env.REACT_APP_BASE_URL;

const Notifications = () => {
  const isAdmin = useSelector((s) => s.app.isAdmin);

  const [myPref, setMyPref] = useState({ smsEnabled: false, emailEnabled: false, email: '', phoneNumber: '', alertsEnabled: false });
  const [myPrefLoading, setMyPrefLoading] = useState(true);
  const [myPrefSaving, setMyPrefSaving] = useState(false);
  const [applyToAllClients, setApplyToAllClients] = useState(false);
  const [touched, setTouched] = useState({ email: false, phoneNumber: false });

  const emailValid = !myPref.emailEnabled || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(myPref.email);
  const phoneValid = !myPref.smsEnabled || /^\d{10}$/.test(myPref.phoneNumber.replace(/\D/g, ''));
  const isFormValid = emailValid && phoneValid;

  const getToken = async () => authService.getAccessToken();

  const fetchMyPref = useCallback(async () => {
    setMyPrefLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BASE}/api/notificationPreferences`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setMyPref({
          smsEnabled: data.smsEnabled ?? false,
          emailEnabled: data.emailEnabled ?? false,
          email: data.email ?? '',
          phoneNumber: data.phoneNumber ?? '',
          alertsEnabled: data.alertsEnabled ?? false
        });
      }
    } catch { toast.error('Failed to load your preferences.'); }
    finally { setMyPrefLoading(false); }
  }, []);

  useEffect(() => { fetchMyPref(); }, [fetchMyPref]);

  const saveMyPref = async () => {
    setTouched({ email: true, phoneNumber: true });
    if (!isFormValid) return;
    setMyPrefSaving(true);
    try {
      const token = await getToken();
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      if (isAdmin && applyToAllClients) {
        const res = await fetch(`${BASE}/api/notificationPreferences/all-clients`, { method: 'PUT', headers, body: JSON.stringify(myPref) });
        if (!res.ok) throw new Error();
        toast.success('Preferences saved for all clients.');
      } else {
        const res = await fetch(`${BASE}/api/notificationPreferences`, { method: 'PUT', headers, body: JSON.stringify(myPref) });
        if (!res.ok) throw new Error();
        toast.success('Preferences saved.');
      }
    } catch { toast.error('Failed to save preferences.'); }
    finally { setMyPrefSaving(false); }
  };

  return (
    <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
      {myPrefLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h5" sx={{ pb: 2 }}>Notification Preferences</Typography>

          {/* -- Delivery -- */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Delivery</Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <FormControlLabel
                  control={<Switch checked={myPref.emailEnabled} onChange={e => { setMyPref(p => ({ ...p, emailEnabled: e.target.checked })); if (!e.target.checked) setTouched(t => ({ ...t, email: false })); }} color="primary" />}
                  label="Email"
                />
                <FormControlLabel
                  control={<Switch checked={myPref.smsEnabled} onChange={e => { setMyPref(p => ({ ...p, smsEnabled: e.target.checked })); if (!e.target.checked) setTouched(t => ({ ...t, phoneNumber: false })); }} color="primary" />}
                  label="SMS"
                />
              </Stack>

              {myPref.emailEnabled && (
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Email Address</Typography>
                  <TextField
                    size="small"
                    placeholder="you@example.com"
                    value={myPref.email}
                    onChange={e => setMyPref(p => ({ ...p, email: e.target.value }))}
                    onBlur={() => setTouched(t => ({ ...t, email: true }))}
                    error={touched.email && !emailValid}
                    helperText={touched.email && !emailValid ? 'Enter a valid email address.' : ''}
                    sx={{ maxWidth: 280 }}
                  />
                </Stack>
              )}

              {myPref.smsEnabled && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Phone Number</Typography>
                  <TextField
                    size="small"
                    placeholder="5551234567"
                    value={myPref.phoneNumber}
                    onChange={e => setMyPref(p => ({ ...p, phoneNumber: e.target.value }))}
                    onBlur={() => setTouched(t => ({ ...t, phoneNumber: true }))}
                    error={touched.phoneNumber && !phoneValid}
                    helperText={touched.phoneNumber && !phoneValid ? 'Enter a 10-digit phone number.' : ''}
                    sx={{ maxWidth: 280 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 640 }}>
                    By enabling SMS notifications and saving your phone number, you consent to receive text messages
                    from AgTechLogic regarding sprayer alerts and activity updates. Message and data rates may apply.
                    Message frequency varies. Reply STOP at any time to opt out. View our{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                    for more information.
                  </Typography>
                </Stack>
              )}

              {isAdmin && (
                <FormControlLabel
                  control={<Switch checked={applyToAllClients} onChange={e => setApplyToAllClients(e.target.checked)} color="primary" />}
                  label="Apply these preferences to all clients"
                />
              )}
            </Stack>
          </Paper>

          {/* -- Alerts -- */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Alerts</Typography>
              <FormControlLabel
                control={<Switch checked={myPref.alertsEnabled} onChange={e => setMyPref(p => ({ ...p, alertsEnabled: e.target.checked }))} color="primary" />}
                label="Opt in to real-time sprayer alerts"
              />
            </Stack>
          </Paper>

          {/* -- Reports -- */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6">Reports</Typography>
                <Chip label="Coming Soon" size="small" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Scheduled reports (daily summaries, weekly usage, etc.) will be available here in a future update.
              </Typography>
            </Stack>
          </Paper>

          {/* -- Actions -- */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={saveMyPref}
              disabled={myPrefSaving || !isFormValid}
              startIcon={myPrefSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            >
              Save Changes
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default Notifications;
